/**
 * Feeding CompiledCharacters into the sim. NO sim contract change is needed:
 *
 *   const compiled = party.map((c) => compileCharacter(classDefOf(c), gearOf(c), loadoutOf(c)));
 *   const content  = buildPartyRegistry(getContentBundle(), compiled);
 *   const sim      = createSim({ seed, content, dungeonId, keystone,
 *                                party: compiled.map((c) => ({ classId: c.classId, name, controller })) });
 *   // bots: createBots(...) / harness runDungeon({ content, party: classIds }) take the same registry.
 *
 * Why it works (sim/src/core):
 *  - `ops.spawnPlayer` (world.ts makePlayerUnit) reads everything from `content.class(classId)`:
 *    `maxHp` → unit hp/maxHp, `baseStats` → unit.stats, `melee`, `resources`, `moveSpeed`.
 *    compileCharacter writes the final stats into `classDef.baseStats` / `classDef.maxHp`.
 *  - createSim applies every `ClassDef.passives` aura at spawn; compile appends gem/talent/
 *    legendary/trinket/expertise/spirit auras there (with persistThroughDeath so they survive
 *    the death → revive path, which does NOT re-apply passives).
 *  - Casting/combat look abilities and auras up by id in the registry, so patched AbilityDefs
 *    replace the originals simply by replacing the ClassDef in the bundle. Ability/class ids
 *    are unchanged, so bot kits (keyed by CLASS/ABILITY ids) keep working.
 *  - Standalone auras (absorb shields referenced only by id) go to `bundle.auras`.
 *
 * Limitation: the registry is per-sim and keyed by class id, so two party members of the SAME
 * class share one ClassDef (talents AND stats). The Profile holds one character per class, so
 * this only matters for mirror comps; throw rather than silently pick one. Per-unit overrides
 * would need an additive `PartyMemberConfig` field (see report), not needed for the MVP.
 */
import type { ContentBundle, ContentRegistry } from '@keystone/sim';
import { createRegistry } from '@keystone/sim';
import type { CompiledCharacter } from './types';

/** Bundle with compiled ClassDefs swapped in (pure; the input bundle is not mutated). */
export function bundleWithCompiled(bundle: ContentBundle, compiled: readonly CompiledCharacter[]): ContentBundle {
  const byClass = new Map<string, CompiledCharacter>();
  for (const c of compiled) {
    const prev = byClass.get(c.classId);
    if (prev && prev !== c) throw new Error(`two different compiled characters for class ${c.classId}: the sim registry holds one ClassDef per class`);
    byClass.set(c.classId, c);
  }
  // +specs: a spec-keyed companion ("warrior@ironclad") is added beside its base; anything else must exist.
  const added: CompiledCharacter[] = [];
  for (const [id, c] of byClass) {
    if (bundle.classes.some((k) => k.id === id)) continue;
    if (!c.classDef.specId) throw new Error(`compiled class ${id} is not in the bundle`);
    added.push(c);
  }
  const auraIds = new Set((bundle.auras ?? []).map((a) => a.id));
  const extra = [...byClass.values()].flatMap((c) => c.registerAuras).filter((a) => !auraIds.has(a.id) && (auraIds.add(a.id), true));
  return {
    ...bundle,
    classes: [...bundle.classes.map((k) => byClass.get(k.id)?.classDef ?? k), ...added.map((c) => c.classDef)],
    auras: [...(bundle.auras ?? []), ...extra],
  };
}

/** Registry ready for `createSim({ content })`, bots and the harness. */
export function buildPartyRegistry(bundle: ContentBundle, compiled: readonly CompiledCharacter[]): ContentRegistry {
  return createRegistry(bundleWithCompiled(bundle, compiled));
}
